import * as React from 'react';
import Grid from '@mui/material/Grid';
import TransactionIndex from './transactions/TransactionIndex';
import transactions from "../models/transactions";
import {useEffect, useState} from "react";
import PageHeader from './layout/PageHeader';
import SectionCard from './layout/SectionCard';
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, FormHelperText, InputLabel, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import TableToolbar from './layout/TableToolbar';
import moment from "moment";
import MetricChart from "./stats/MetricChart";
import stats from "../models/stats";
import useRootHook from "../hooks/useRoot";
import helpers from "../helpers/common";
import referrals from "../models/referrals";
import methods from "../models/payout-methods";
import payouts from "../models/payouts";
import { useNavigate } from "react-router-dom";
import NewsWidget from "./news/NewsWidget";

const DATE_FORMAT = 'DD/MM/YYYY';

const sumStatsAmount = (resp) => {
    if (!resp?.stats?.length) return 0;
    const subunit = resp.subunit || 1;
    const raw = resp.stats.reduce((sum, row) => sum + parseFloat(row.value || 0), 0);
    return parseFloat((raw / subunit).toFixed(2));
};

const emptyPeriodTotals = () => ({
    total: { amount: 0 },
    commissions: { amount: 0 },
    payouts: { amount: 0 },
    refunds: { amount: 0 },
    disputed: { amount: 0 },
    blocked: { amount: 0 },
    disputeCost: { amount: 0 },
    refundCost: { amount: 0 },
});

const KpiTile = ({ title, value }) => {
    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.default',
                px: 1.5,
                py: 1.25,
                minHeight: 64,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
            }}
        >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.02em' }}>
                {title}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                {value}
            </Typography>
        </Box>
    )
}

export default function Dashboard() {
    const {userData} = useRootHook();
    const [totals, setTotals] = useState(null)
    const [periodTotals, setPeriodTotals] = useState(null)
    const [periodTotalsLoading, setPeriodTotalsLoading] = useState(false)
    const [todayNet, setTodayNet] = useState(null)
    const [range, setRange] = useState('week')
    const [filters, setFilters] = useState(() => {
        const to = moment.utc()
        const from = moment.utc().clone().subtract(1, 'week')
        return { from: from.startOf('day').toISOString(), to: to.endOf('day').toISOString() }
    })

    const [referralStats, setReferralStats] = useState(null)
    const [payoutMethods, setPayoutMethods] = useState(null)
    const [payoutDialogOpen, setPayoutDialogOpen] = useState(false)
    const [payoutFields, setPayoutFields] = useState({ amount_total: '', method_id: '', wallet_address: '' })
    const [payoutErrors, setPayoutErrors] = useState({})
    const navigate = useNavigate()
    const { setShowLoader, notify } = useRootHook()

    useEffect(() => {
        // For referrals, load referral stats
        if (userData?.role === helpers.roles.R) {
            setShowLoader(true)
            const filters = {
                from: moment().subtract(30, 'day').format('YYYY-MM-DD'),
                to: moment().format('YYYY-MM-DD')
            }
            referrals.getStats(filters)
                .then((data) => {
                    data && setReferralStats(data)
                    setShowLoader(false)
                }).catch(() => setShowLoader(false))
            
            // Load payout methods
            methods.list()
                .then((data) => {
                    data && setPayoutMethods(data)
                })
            return;
        }
        transactions.getTotals()
            .then((totals) => {
                totals && setTotals(totals)
            })
    }, [userData?.role])

    useEffect(() => {
        if (userData?.role === helpers.roles.R) return

        let cancelled = false
        setPeriodTotalsLoading(true)

        const base = { from: filters.from, to: filters.to }
        const todayFrom = moment.utc().startOf('day').toISOString()
        const todayTo = moment.utc().endOf('day').toISOString()
        const isUtcTodayOnly = base.from === todayFrom && base.to === todayTo

        Promise.all([
            stats.getStats({ ...base, type: 'netAmount' }),
            stats.getStats({ ...base, type: 'refundedAmount' }),
            stats.getStats({ ...base, type: 'disputedAmount' }),
            stats.getStats({ ...base, type: 'dashboardPeriodTotals' }),
            isUtcTodayOnly
                ? Promise.resolve(null)
                : stats.getStats({ type: 'netAmount', from: todayFrom, to: todayTo }),
        ])
            .then(([net, refunded, disputed, extra, todayNetResp]) => {
                if (cancelled) return
                const hasExtra = extra?.commissions?.amount != null
                const netTotal = sumStatsAmount(net)
                setPeriodTotals({
                    total: { amount: netTotal },
                    refunds: { amount: sumStatsAmount(refunded) },
                    disputed: { amount: sumStatsAmount(disputed) },
                    commissions: hasExtra ? extra.commissions : { amount: 0 },
                    payouts: hasExtra ? extra.payouts : { amount: 0 },
                    blocked: hasExtra ? extra.blocked : { amount: 0 },
                    disputeCost: hasExtra ? extra.disputeCost : { amount: 0 },
                    refundCost: hasExtra ? extra.refundCost : { amount: 0 },
                })
                // Same metric as period Net — never mix with completed-only / gross
                setTodayNet(isUtcTodayOnly ? netTotal : sumStatsAmount(todayNetResp))
            })
            .catch(() => {
                if (!cancelled) {
                    setPeriodTotals(emptyPeriodTotals())
                    setTodayNet(null)
                }
            })
            .finally(() => {
                if (!cancelled) setPeriodTotalsLoading(false)
            })

        return () => { cancelled = true }
    }, [filters.from, filters.to, userData?.role])

    // For referrals, show referral dashboard
    if (userData?.role === helpers.roles.R) {
        if (!referralStats) return null

        const totals = referralStats.totals || {}
        const merchants = referralStats.merchants || []
        const defaultCurrency = userData?.defaultCurrency
        const totalEarnings = totals.total_earnings || 0

        const handlePayoutChange = (ev) => {
            const [, value, name] = helpers.getFormEventData(ev)
            setPayoutErrors({...payoutErrors, [name]: undefined})
            setPayoutFields({...payoutFields, [name]: value})
        }

        const handlePayoutBlur = (ev) => {
            const [, , name] = helpers.getFormEventData(ev)
            const newErrors = {...payoutErrors}
            const newFields = {...payoutFields}
            if (typeof payouts.fieldValidator[name] === 'function') {
                payouts.fieldValidator[name](newFields, newErrors)
            }
            setPayoutErrors(newErrors)
            setPayoutFields(newFields)
        }

        const handlePayoutSubmit = (ev) => {
            ev.preventDefault()
            const validationErrors = payouts.methodValidator.add({...payoutFields}, {})
            setPayoutErrors(validationErrors)

            if (Object.keys(validationErrors).length !== 0) return

            setShowLoader(true)
            payouts.add(payoutFields)
                .then(() => {
                    setShowLoader(false)
                    setPayoutDialogOpen(false)
                    setPayoutFields({ amount_total: '', method_id: '', wallet_address: '' })
                    notify('Payout request created successfully', 3000)
                    // Refresh stats
                    referrals.getStats({
                        from: moment().subtract(30, 'day').format('YYYY-MM-DD'),
                        to: moment().format('YYYY-MM-DD')
                    }).then((data) => {
                        data && setReferralStats(data)
                    })
                }).catch(() => setShowLoader(false))
        }

        return <React.Fragment>
            <Grid size={12}>
                <PageHeader 
                    title="Dashboard" 
                    subtitle="Your referral earnings and statistics"
                />
            </Grid>
            
            <Grid size={12}>
                <SectionCard>
                    <Stack spacing={2}>
                        <Typography variant="h6">Total Earnings</Typography>
                        <Typography variant="h3" sx={{ fontWeight: 900, color: 'primary.main' }}>
                            {defaultCurrency ? helpers.getSubunitAmountString(totalEarnings, defaultCurrency.symbol, defaultCurrency.subunit) : totalEarnings}
                        </Typography>
                        <Button 
                            variant="contained" 
                            size="large"
                            onClick={() => setPayoutDialogOpen(true)}
                            disabled={totalEarnings <= 0}
                            sx={{ mt: 2, maxWidth: 300 }}
                        >
                            Request Payout
                        </Button>
                    </Stack>
                </SectionCard>
            </Grid>

            <Grid size={12}>
                <SectionCard>
                    <Stack spacing={2}>
                        <Typography variant="h6">Statistics</Typography>
                        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                                <Typography variant="h6">
                                    {defaultCurrency ? helpers.getSubunitAmountString(totals.total_amount || 0, defaultCurrency.symbol, defaultCurrency.subunit) : totals.total_amount || 0}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Total Transactions</Typography>
                                <Typography variant="h6">{totals.total_transactions || 0}</Typography>
                            </Box>
                        </Box>
                    </Stack>
                </SectionCard>
            </Grid>

            <Grid size={12}>
                <SectionCard sx={{ display: 'flex', flexDirection: 'column', overflowX: 'auto'}}>
                    <TableToolbar title="Merchants Statistics" />
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Merchant</TableCell>
                                <TableCell>Username</TableCell>
                                <TableCell align="right">Percentage</TableCell>
                                <TableCell align="right">Total Amount</TableCell>
                                <TableCell align="right">Transactions</TableCell>
                                <TableCell align="right">Your Earnings</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {merchants.map((row) => (
                                <TableRow key={`merchant-${row.merchant_id}`}>
                                    <TableCell>{row.merchant_name}</TableCell>
                                    <TableCell>{row.merchant_username}</TableCell>
                                    <TableCell align="right">{row.percentage}%</TableCell>
                                    <TableCell align="right">
                                        {defaultCurrency ? helpers.getSubunitAmountString(row.total_amount || 0, defaultCurrency.symbol, defaultCurrency.subunit) : row.total_amount || 0}
                                    </TableCell>
                                    <TableCell align="right">{row.transactions_count || 0}</TableCell>
                                    <TableCell align="right">
                                        {defaultCurrency ? helpers.getSubunitAmountString(row.referral_earnings || 0, defaultCurrency.symbol, defaultCurrency.subunit) : row.referral_earnings || 0}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {merchants.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        No merchants assigned
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </SectionCard>
            </Grid>

            <Dialog open={payoutDialogOpen} onClose={() => setPayoutDialogOpen(false)} maxWidth="sm" fullWidth>
                <form onSubmit={handlePayoutSubmit}>
                    <DialogTitle>Request Payout</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <Box>
                                <InputLabel>Method</InputLabel>
                                <Select
                                    fullWidth
                                    error={!!payoutErrors.method_id}
                                    required
                                    name="method_id"
                                    value={payoutFields.method_id || ""}
                                    onChange={handlePayoutChange}
                                    onBlur={handlePayoutBlur}
                                >
                                    {payoutMethods && payoutMethods.map((method) => (
                                        <MenuItem key={`id-${method.id}`} value={method.id}>
                                            {`${method.name} - fee ${method.fee}%`}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {payoutErrors.method_id && <FormHelperText error>{payoutErrors.method_id}</FormHelperText>}
                            </Box>
                            <Box>
                                <InputLabel>Amount</InputLabel>
                                <TextField
                                    fullWidth
                                    error={!!payoutErrors.amount_total}
                                    required
                                    name="amount_total"
                                    type="number"
                                    value={payoutFields.amount_total}
                                    onChange={handlePayoutChange}
                                    onBlur={handlePayoutBlur}
                                    placeholder="Enter amount"
                                    inputProps={{ min: 0, step: 0.01 }}
                                />
                                {payoutErrors.amount_total && <FormHelperText error>{payoutErrors.amount_total}</FormHelperText>}
                            </Box>
                            <Box>
                                <InputLabel>Wallet Address</InputLabel>
                                <TextField
                                    fullWidth
                                    error={!!payoutErrors.wallet_address}
                                    required
                                    name="wallet_address"
                                    value={payoutFields.wallet_address}
                                    onChange={handlePayoutChange}
                                    onBlur={handlePayoutBlur}
                                    placeholder="Ex: 0x..."
                                />
                                {payoutErrors.wallet_address && <FormHelperText error>{payoutErrors.wallet_address}</FormHelperText>}
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setPayoutDialogOpen(false)}>Cancel</Button>
                        <Button type="submit" variant="contained">Request Payout</Button>
                    </DialogActions>
                </form>
            </Dialog>
        </React.Fragment>
    }

    if (!totals) return null

    const symbol = totals.defaultCurrency.symbol
    const isAdmin = [helpers.roles.A, helpers.roles.S].includes(userData?.role)
    const dayStats = totals.dayStatusStats || { success: 0, declined: 0, successRate: 0 }

    const rangeLabels = {
        day: 'today',
        week: '7 days',
        month: '30 days',
    }
    const periodLabel = range
        ? rangeLabels[range]
        : `${moment.utc(filters.from).format('DD MMM YYYY')} – ${moment.utc(filters.to).format('DD MMM YYYY')}`
    const period = periodTotals || emptyPeriodTotals()
    const fmtMoney = (n) => `${Number(n || 0).toFixed(1)}${symbol}`
    const fmtPeriod = (n) => (periodTotalsLoading ? '…' : fmtMoney(n))
    const isUtcTodayOnly =
        moment.utc(filters.from).isSame(moment.utc(), 'day')
        && moment.utc(filters.to).isSame(moment.utc(), 'day')
    // Single source when viewing today: Today card === period Net
    const todayVolume = isUtcTodayOnly && periodTotals && !periodTotalsLoading
        ? period.total.amount
        : todayNet
    const kpiGridSx = {
        display: 'grid',
        gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            sm: 'repeat(4, minmax(0, 1fr))',
        },
        gap: 2,
    }

    const applyFilters = (from, to, nextRange) => {
        setRange(nextRange)
        setPeriodTotals(null)
        setFilters({
            from: from.clone().startOf('day').toISOString(),
            to: to.clone().endOf('day').toISOString(),
        })
    }

    const handleRange = (_ev, next) => {
        if (!next) return
        const to = moment.utc()
        const from =
            next === 'day'
                ? moment.utc().startOf('day')
                : next === 'month'
                    ? moment.utc().clone().subtract(30, 'days')
                    : moment.utc().clone().subtract(7, 'days')
        applyFilters(from, to, next)
    }

    const handleDateFrom = (newValue) => {
        if (!newValue) return
        const from = moment.utc(newValue)
        let to = moment.utc(filters.to)
        if (to.isBefore(from)) to = from.clone()
        applyFilters(from, to, null)
    }

    const handleDateTo = (newValue) => {
        if (!newValue) return
        let from = moment.utc(filters.from)
        const to = moment.utc(newValue)
        if (from.isAfter(to)) from = to.clone()
        applyFilters(from, to, null)
    }

    return <React.Fragment>
        <NewsWidget />
        <Grid size={12}>
            <PageHeader title="Dashboard" subtitle="Overview of transactions, balances and recent activity" />
        </Grid>

        <Grid size={{ xs: 12, lg: 8 }}>
            <SectionCard sx={{ p: { xs: 2, md: 2.5 } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 1.5 }}>
                    <Stack spacing={0.25}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                            Payment volume
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Net volume over selected period (excl. new & declined)
                        </Typography>
                    </Stack>
                </Stack>
                <MetricChart title="" type="netAmount" filters={filters} height={300} />
            </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
            <Stack spacing={{ xs: 2, md: 3 }}>
                <SectionCard>
                    <Stack spacing={0.75}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                            Today
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>
                            {periodTotalsLoading && isUtcTodayOnly ? '…' : todayVolume == null ? '…' : `${symbol}${Number(todayVolume).toFixed(1)}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {moment.utc().format('DD MMMM, YYYY')} · net (excl. new & declined)
                        </Typography>
                        {isAdmin && (
                            <Stack spacing={0.5} sx={{ pt: 1 }}>
                                <Typography variant="body2">
                                    Success: {dayStats.success}
                                </Typography>
                                <Typography variant="body2">
                                    Declined: {dayStats.declined}
                                </Typography>
                                <Typography variant="body2">
                                    Success Rate: {dayStats.successRate}%
                                </Typography>
                            </Stack>
                        )}
                    </Stack>
                </SectionCard>
                <SectionCard>
                    <MetricChart title="Payments count" type="grossCount" filters={filters} height={200} />
                </SectionCard>
            </Stack>
        </Grid>

        {/* All-time + current balance */}
        <Grid size={12}>
            <SectionCard>
                <Stack spacing={1.5}>
                    <Stack spacing={0.25}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                            All-time statistics
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Turnover and movements for the entire period (Net = excl. new & declined)
                        </Typography>
                    </Stack>
                    <Box sx={kpiGridSx}>
                        <KpiTile title="Net" value={fmtMoney(totals.allTimeTotal.amount)} />
                        <KpiTile title="Commissions" value={fmtMoney(totals.allTimeCommissions.amount)} />
                        <KpiTile title="Payouts" value={fmtMoney(totals.allTimePayouts.amount)} />
                        <KpiTile
                            title="Refunds / Disputed / Blocked"
                            value={`${fmtMoney(totals.allTimeRefunds.amount)} / ${fmtMoney(totals.allTimeDisputed.amount)} / ${fmtMoney((totals.allTimeBlocked && totals.allTimeBlocked.amount) || 0)}`}
                        />
                        <KpiTile title="Dispute cost" value={fmtMoney(totals.allTimeDisputeCost.amount)} />
                        <KpiTile title="Refund cost" value={fmtMoney((totals.allTimeRefundCost && totals.allTimeRefundCost.amount) || 0)} />
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 900, pt: 1 }}>
                        Current balance
                    </Typography>
                    <Box sx={kpiGridSx}>
                        <KpiTile title="Rolling / Hold" value={`${fmtMoney(totals.rollingReserve.amount)} / ${fmtMoney(totals.holdAmount.amount)}`} />
                        <KpiTile title="Balance" value={fmtMoney(totals.totalBalance)} />
                        <KpiTile title="Available" value={fmtMoney(totals.availableBalance)} />
                    </Box>
                </Stack>
            </SectionCard>
        </Grid>

        {/* Period statistics with date range */}
        <Grid size={12}>
            <SectionCard>
                <Stack spacing={1.5}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                        justifyContent="space-between"
                        spacing={1.5}
                    >
                        <Stack spacing={0.25}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                                Statistics for {periodLabel}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Turnover and movements for the selected period (Net = excl. new & declined)
                            </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                            <ToggleButtonGroup
                                value={range}
                                size="small"
                                exclusive
                                onChange={handleRange}
                                aria-label="Date range"
                            >
                                <ToggleButton value="day">Today</ToggleButton>
                                <ToggleButton value="week">7 days</ToggleButton>
                                <ToggleButton value="month">30 days</ToggleButton>
                            </ToggleButtonGroup>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    label="From"
                                    disableFuture
                                    value={moment.utc(filters.from)}
                                    format={DATE_FORMAT}
                                    onChange={handleDateFrom}
                                    slotProps={{ textField: { size: 'small' } }}
                                />
                                <DatePicker
                                    label="To"
                                    disableFuture
                                    value={moment.utc(filters.to)}
                                    format={DATE_FORMAT}
                                    onChange={handleDateTo}
                                    slotProps={{ textField: { size: 'small' } }}
                                />
                            </LocalizationProvider>
                        </Stack>
                    </Stack>
                    <Box sx={kpiGridSx}>
                        <KpiTile title="Net" value={fmtPeriod(period.total.amount)} />
                        <KpiTile title="Commissions" value={fmtPeriod(period.commissions.amount)} />
                        <KpiTile title="Payouts" value={fmtPeriod(period.payouts.amount)} />
                        <KpiTile
                            title="Refunds / Disputed / Blocked"
                            value={periodTotalsLoading
                                ? '…'
                                : `${fmtMoney(period.refunds.amount)} / ${fmtMoney(period.disputed.amount)} / ${fmtMoney(period.blocked.amount)}`}
                        />
                        <KpiTile title="Dispute cost" value={fmtPeriod(period.disputeCost.amount)} />
                        <KpiTile title="Refund cost" value={fmtPeriod((period.refundCost && period.refundCost.amount) || 0)} />
                    </Box>
                </Stack>
            </SectionCard>
        </Grid>

        <Grid size={12}>
            <SectionCard sx={{ display: 'flex', flexDirection: 'column', overflowX: 'auto'}}>
                <TransactionIndex fullPage={false} filters={{status: transactions.statusToEnum.completed}}/>
            </SectionCard>
        </Grid>
    </React.Fragment>
}